import { useState } from 'react';
import {
  globalActivityEntries,
  activityTypeConfigs,
  filterConfigs,
  globalActivityStats,
  activityRules,
  getActivitiesByFilter,
  getActivityDistribution,
  getFilterDistribution,
} from '@/mocks/rfwGlobalActivity';

export default function ActividadGlobalSection() {
  const [activeTab, setActiveTab] = useState<'feed' | 'filtros' | 'estadisticas' | 'reglas'>('feed');
  const [selectedFilter, setSelectedFilter] = useState('todo');

  const filteredActivities = getActivitiesByFilter(selectedFilter).slice(0, 8);
  const typeDistribution = getActivityDistribution();
  const filterDistribution = getFilterDistribution();

  function timeAgo(timestamp: string): string {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days}d`;
  }

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 border border-teal-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-teal-700 tracking-wide">ACTIVIDAD GLOBAL</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            RFW está <span className="bg-gradient-to-r from-teal-500 via-emerald-500 to-amber-500 bg-clip-text text-transparent">viva</span> ahora mismo
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Comentarios, nuevas radios, concursos, ganadores, eventos, clubes. Todo lo que está pasando en la comunidad en este momento.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-8">
          {[
            { value: globalActivityStats.totalHoy.toLocaleString(), label: 'Actividad Hoy', icon: 'ri-flashlight-line', color: 'text-teal-600', bg: 'bg-teal-50' },
            { value: globalActivityStats.ahoraActivos.toString(), label: 'Ahora Mismo', icon: 'ri-user-voice-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { value: globalActivityStats.totalSemana.toLocaleString(), label: 'Esta Semana', icon: 'ri-calendar-check-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { value: globalActivityStats.promedioPorHora.toString(), label: 'Por Hora', icon: 'ri-bar-chart-line', color: 'text-rose-600', bg: 'bg-rose-50' },
            { value: globalActivityStats.diaMasActivo, label: 'Día Pico', icon: 'ri-fire-line', color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map((stat, i) => (
            <div key={i} className={`${stat.bg} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
              <div className="flex items-center justify-center gap-1 text-xs text-foreground-500 mt-1">
                <i className={`${stat.icon} text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {(['feed', 'filtros', 'estadisticas', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-teal-500 text-white'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'feed' && 'Feed Global'}
                {tab === 'filtros' && 'Filtros'}
                {tab === 'estadisticas' && 'Estadísticas'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Feed Global */}
        {activeTab === 'feed' && (
          <div>
            {/* Type filter chips */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 mb-6">
              <span className="text-xs text-foreground-500 mr-1">Filtrar:</span>
              {filterConfigs.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSelectedFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                    selectedFilter === f.id
                      ? 'bg-teal-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-teal-100 border border-background-200/70'
                  }`}
                >
                  <i className={`${f.icon} text-xs`}></i>
                  {f.label}
                  <span className={`text-xs ml-0.5 ${selectedFilter === f.id ? 'text-teal-100' : 'text-foreground-400'}`}>
                    {f.id === 'todo' ? globalActivityEntries.length : getActivitiesByFilter(f.id).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Activity Feed */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredActivities.map((activity) => {
                const typeCfg = activityTypeConfigs.find((t) => t.id === activity.type);
                const filterCfg = filterConfigs.find((f) => f.id === activity.filterId);
                return (
                  <div key={activity.id} className="bg-background-50 rounded-lg border border-background-200/70 hover:border-teal-200 transition-colors p-4 flex gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-background-200/70">
                        <img src={activity.user.avatar} alt={activity.user.name} className="w-full h-full object-cover" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="font-semibold text-sm text-foreground-950 truncate">{activity.user.name}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5 bg-gradient-to-r ${typeCfg?.gradient ?? 'from-gray-400 to-gray-500'} text-white`}>
                          <i className={`${typeCfg?.icon ?? 'ri-circle-line'} text-[10px]`}></i>
                          {typeCfg?.label ?? activity.type}
                        </span>
                      </div>

                      <a
                        href={activity.target.link}
                        className="text-xs font-medium text-teal-600 hover:text-teal-700 cursor-pointer block mb-2"
                      >
                        {activity.target.name}
                        <span className="text-foreground-400 font-normal ml-1">· {activity.target.type}</span>
                      </a>

                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-2">{activity.excerpt}</p>

                      <div className="flex items-center gap-1.5 text-xs text-foreground-400">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-time-line"></i>
                          {timeAgo(activity.timestamp)}
                        </span>
                        <span className="text-foreground-300">·</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-heart-line"></i>
                          {activity.reactionsCount}
                        </span>
                        <span className="text-foreground-300">·</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-chat-3-line"></i>
                          {activity.commentsCount}
                        </span>
                        <span className="text-foreground-300">·</span>
                        <span className={`flex items-center gap-0.5 ${filterCfg?.color === 'orange' ? 'text-orange-500' : filterCfg?.color === 'violet' ? 'text-violet-500' : filterCfg?.color === 'sky' ? 'text-sky-500' : filterCfg?.color === 'fuchsia' ? 'text-fuchsia-500' : filterCfg?.color === 'amber' ? 'text-amber-500' : 'text-foreground-400'}`}>
                          <i className={`${filterCfg?.icon ?? 'ri-global-line'} text-xs`}></i>
                          {filterCfg?.label ?? activity.filterId}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredActivities.length === 0 && (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-background-200/50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">No hay actividad en este filtro ahora mismo.</p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Filtros */}
        {activeTab === 'filtros' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {filterConfigs.map((f) => {
                const count = f.id === 'todo' ? globalActivityEntries.length : getActivitiesByFilter(f.id).length;
                const sample = f.id !== 'todo' ? getActivitiesByFilter(f.id).slice(0, 3) : globalActivityEntries.slice(0, 3);
                return (
                  <div key={f.id} className="bg-background-50 rounded-lg border border-background-200/70 p-4 text-center hover:border-teal-200 transition-colors">
                    <div className={`w-12 h-12 rounded-xl bg-${f.color}-50 flex items-center justify-center mx-auto mb-3`}>
                      <i className={`${f.icon} text-xl text-${f.color}-500`}></i>
                    </div>
                    <div className="font-semibold text-sm text-foreground-950 mb-1">{f.label}</div>
                    <div className="text-2xl font-bold text-teal-600 mb-2">{count}</div>
                    <div className="text-xs text-foreground-400">actividades</div>
                    <div className="mt-3 space-y-1">
                      {sample.map((a, idx) => (
                        <div key={idx} className="text-[10px] text-foreground-500 truncate text-left">
                          <span className="font-medium">{a.user.name}</span> · {a.type.replace('_', ' ')}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Estadísticas */}
        {activeTab === 'estadisticas' && (
          <div>
            {/* Live Activity Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3 mb-6">
              {[
                { label: 'Comentando', value: globalActivityStats.ahoraComentando, icon: 'ri-chat-3-line', bg: 'bg-sky-50', color: 'text-sky-600' },
                { label: 'Subiendo', value: globalActivityStats.ahoraSubiendo, icon: 'ri-upload-cloud-2-line', bg: 'bg-emerald-50', color: 'text-emerald-600' },
                { label: 'Compitiendo', value: globalActivityStats.ahoraCompitiendo, icon: 'ri-trophy-line', bg: 'bg-amber-50', color: 'text-amber-600' },
                { label: 'Ganando', value: globalActivityStats.ahoraGanadores, icon: 'ri-medal-line', bg: 'bg-rose-50', color: 'text-rose-600' },
                { label: 'Eventos', value: globalActivityStats.ahoraEventos, icon: 'ri-calendar-event-line', bg: 'bg-violet-50', color: 'text-violet-600' },
                { label: 'Radios Nuevas', value: globalActivityStats.ahoraNuevasRadios, icon: 'ri-radio-line', bg: 'bg-orange-50', color: 'text-orange-600' },
                { label: 'Clubes Nuevos', value: globalActivityStats.ahoraNuevosClubes, icon: 'ri-group-line', bg: 'bg-fuchsia-50', color: 'text-fuchsia-600' },
              ].map((item, i) => (
                <div key={i} className={`${item.bg} rounded-lg p-3 text-center`}>
                  <i className={`${item.icon} ${item.color} text-xl block mb-1`}></i>
                  <div className={`text-lg font-bold ${item.color}`}>{item.value}</div>
                  <div className="text-xs text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>

            {/* Type Distribution */}
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 mb-4">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Distribución por Tipo de Actividad</div>
              <div className="space-y-2">
                {typeDistribution.map((item) => {
                  const maxCount = Math.max(...typeDistribution.map((d) => d.count));
                  const barWidth = (item.count / maxCount) * 100;
                  return (
                    <div key={item.type} className="flex items-center gap-3">
                      <div className="w-28 flex items-center gap-1.5 flex-shrink-0">
                        <i className={`${item.config?.icon ?? 'ri-circle-line'} text-sm ${item.config?.color === 'sky' ? 'text-sky-500' : item.config?.color === 'emerald' ? 'text-emerald-500' : item.config?.color === 'amber' ? 'text-amber-500' : item.config?.color === 'rose' ? 'text-rose-500' : item.config?.color === 'violet' ? 'text-violet-500' : item.config?.color === 'orange' ? 'text-orange-500' : item.config?.color === 'fuchsia' ? 'text-fuchsia-500' : 'text-foreground-500'}`}></i>
                        <span className="text-xs text-foreground-600">{item.config?.label ?? item.type}</span>
                      </div>
                      <div className="flex-1 h-4 bg-background-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${item.config?.gradient ?? 'from-gray-400 to-gray-500'}`}
                          style={{ width: `${barWidth}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-semibold text-foreground-700 w-8 text-right">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Entity totals */}
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Entidades Activas en RFW</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center bg-orange-50 rounded-lg p-3">
                  <i className="ri-radio-line text-lg text-orange-500 block mb-1"></i>
                  <div className="text-lg font-bold text-foreground-950">{globalActivityStats.totalEntidades.radios.toLocaleString()}</div>
                  <div className="text-xs text-foreground-400">Radios</div>
                </div>
                <div className="text-center bg-violet-50 rounded-lg p-3">
                  <i className="ri-calendar-check-line text-lg text-violet-500 block mb-1"></i>
                  <div className="text-lg font-bold text-foreground-950">{globalActivityStats.totalEntidades.programas.toLocaleString()}</div>
                  <div className="text-xs text-foreground-400">Programas</div>
                </div>
                <div className="text-center bg-sky-50 rounded-lg p-3">
                  <i className="ri-mic-line text-lg text-sky-500 block mb-1"></i>
                  <div className="text-lg font-bold text-foreground-950">{globalActivityStats.totalEntidades.locutores.toLocaleString()}</div>
                  <div className="text-xs text-foreground-400">Locutores</div>
                </div>
                <div className="text-center bg-fuchsia-50 rounded-lg p-3">
                  <i className="ri-group-line text-lg text-fuchsia-500 block mb-1"></i>
                  <div className="text-lg font-bold text-foreground-950">{globalActivityStats.totalEntidades.clubes.toLocaleString()}</div>
                  <div className="text-xs text-foreground-400">Clubes</div>
                </div>
                <div className="text-center bg-amber-50 rounded-lg p-3">
                  <i className="ri-calendar-event-line text-lg text-amber-500 block mb-1"></i>
                  <div className="text-lg font-bold text-foreground-950">{globalActivityStats.totalEntidades.eventos.toLocaleString()}</div>
                  <div className="text-xs text-foreground-400">Eventos</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {activityRules.map((rule) => (
                <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-teal-200 transition-colors">
                  <div className={`w-10 h-10 rounded-full bg-${rule.color}-50 flex items-center justify-center mb-3`}>
                    <i className={`${rule.icon} text-lg text-${rule.color}-500`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <div className="text-xs text-foreground-600 leading-relaxed">{rule.description}</div>
                </div>
              ))}
            </div>

            {/* Filter distribution */}
            <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Actividad por Categoría</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {filterDistribution.filter((f) => f.config).map((f) => (
                  <div key={f.filterId} className="bg-background-100 rounded-lg p-3 text-center">
                    <i className={`${f.config?.icon ?? 'ri-global-line'} ${f.filterId === 'todo' ? 'text-teal-500' : f.filterId === 'radios' ? 'text-orange-500' : f.filterId === 'programas' ? 'text-violet-500' : f.filterId === 'locutores' ? 'text-sky-500' : f.filterId === 'clubes' ? 'text-fuchsia-500' : 'text-amber-500'} text-lg mb-1 block`}></i>
                    <div className="text-lg font-bold text-foreground-950">{f.count}</div>
                    <div className="text-xs text-foreground-400">{f.config?.label ?? f.filterId}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/actividad-global"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white rounded-full text-sm font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-global-line"></i>
            Ver toda la actividad global
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">Siempre mostrar actividad reciente real.</p>
        </div>
      </div>
    </section>
  );
}